import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import lodashPadStart from 'lodash/padStart';

import {
  makeListingFullAddressString,
} from 'common/listingHelpers';

import {
  ACCOUNT_TYPE_BANK,
  ACCOUNT_TYPE_CREDIT_DEBIT_CARD,
} from 'common/financeConstants';

import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_BULLET_COLOR_YELLOW,
} from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrInfoTile from 'client/components/SrInfoTile/SrInfoTile';
import SrExternalLink from 'client/components/SrExternalLink';
import SrPartnerapprovalFormFeesCard from 'client/components/SrPartnerApproval/SrPartnerapprovalFormFeesCard';

const SrLocalInfoTile = ({ marginTop = true, value, ...props }) => (
  <SrBox marginTop={marginTop ? '21px' : '0px'}>
    <SrInfoTile
      value={Array.isArray(value)
        ? lodashCompact(value).join('\n')
        : value}
      {...props}
    />
  </SrBox>
);

SrLocalInfoTile.displayName = 'SrLocalInfoTile';

const SrConnectBankAccountFormSummary = ({
  formikBag: { values },
  onEditStep,
}) => {
  return (
    (
      <>
        {values.household && (
          <SrPartnerapprovalFormFeesCard
            partnerSlug={values.household.partnerSlug}
            listing={values.household.listing}
            householdListing={values.household.householdListing}
            householdApplications={values.household.householdApplications}
          />
        )}
        <SrCard color={SR_CARD_BULLET_COLOR_BLUE} header='Account Holder'>
          <SrLocalInfoTile
            marginTop
            label='Contact Info'
            value={
              <>
                {[[values.firstName, values.lastName].join(' '), values.phone].join(', ')}
                <br />
                {values.email}
              </>
            }
            onEditClick={() => onEditStep('accountHolder')}
          />
          <SrLocalInfoTile
            marginTop
            label='Date of Birth'
            value={moment(values.dob).format('MM/DD/YY')}
            onEditClick={() => onEditStep('accountHolder')}
          />
          <SrLocalInfoTile
            marginTop
            label='Last 4 Digits of SSN'
            value={`***-**-${values.ssnLast4}`}
            onEditClick={() => onEditStep('accountHolder')}
          />
          <SrLocalInfoTile
            marginTop
            label='Billing Address'
            value={makeListingFullAddressString(values)}
            onEditClick={() => onEditStep('accountHolder')}
          />
        </SrCard>
        <SrCard color={SR_CARD_BULLET_COLOR_YELLOW} header='Collection Details'>
          <SrLocalInfoTile
            label='Account Type'
            value={values.accountType === ACCOUNT_TYPE_BANK
              ? 'Bank Account'
              : 'Debit Card'
            }
            onEditClick={() => onEditStep('chooseBank')}
          />
          {values.accountType === ACCOUNT_TYPE_BANK && (
            <>
              <SrLocalInfoTile
                label='Routing Number'
                value={lodashPadStart(values.bankRoutingLast4, 8, '*')}
                onEditClick={() => onEditStep('chooseBank')}
              />
              <SrLocalInfoTile
                label='Account Number'
                value={lodashPadStart(values.bankAccountLast4, 8, '*')}
                onEditClick={() => onEditStep('chooseBank')}
              />
            </>
          )}
          {values.accountType === ACCOUNT_TYPE_CREDIT_DEBIT_CARD && (
            <>
              <SrLocalInfoTile
                label='Card Number'
                value={lodashPadStart(values.cardLast4, 8, '*')}
                onEditClick={() => onEditStep('chooseBank')}
              />
              <SrLocalInfoTile
                label='Card Expiration'
                value={values.cardExpiration}
                onEditClick={() => onEditStep('chooseBank')}
              />
            </>
          )}
        </SrCard>
        <SrBox paddingTop='20px' paddingLeft='20px'>
          <SrText
            size='12'
            lineHeight='20px'
            weight='600'
            font='greycliff'
            color='grey400'
          >
            {'By registering your account, you agree to the '}
          </SrText>
          <SrExternalLink
            link='https://stripe.com/connect-account/legal/full'
            target='_blank'
          >
            <SrText
              size='12'
              lineHeight='20px'
              weight='600'
              font='greycliff'
              color='blue300'
              cursor='pointer'
            >
              Stripe Connected Account Agreement.
            </SrText>
          </SrExternalLink>
        </SrBox>
      </>
    )
  );
};

SrConnectBankAccountFormSummary.propTypes = {
  formikBag: PropTypes.object.isRequired,
  onEditStep: PropTypes.func.isRequired,
};

SrConnectBankAccountFormSummary.displayName = 'SrConnectBankAccountFormSummary';

export default SrConnectBankAccountFormSummary;
